import { scoredSummary, usableSummary } from "./post-session-overview";

// Cross-student learning activity for the tutor dashboard, built only from the
// list calls the page already makes once for all students: post-session quiz
// rows and OPEN question tags. Self-started and tutor-assigned quizzes are only
// readable per student, so they are deliberately not here (no fan-out); the
// coverage copy says so instead of implying a complete feed.

export const POST_SESSION = "post-session";
export const TAG = "tag";

// Timestamps arrive as UTC ISO-8601 ("…Z"). Anything else is unavailable rather
// than parsed leniently into a plausible-looking date.
export const parseUtc = (value) => {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(value)) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const validTime = (value) => (parseUtc(value) ? value : null);
const idOf = (value) => (value === undefined || value === null || value === "" ? null : String(value));

// Learning = the student did something in QLearn. Sends, link opens (possibly an
// email scanner), expiry and withdrawal are lifecycle, shown in the tracker only.
export const postSessionEvents = (rows) => (Array.isArray(rows) ? rows : []).flatMap((row) => {
  if (!row || row.engagementVisible !== true || !["COMPLETED", "CLAIMED"].includes(row.state)) return [];
  const completed = row.state === "COMPLETED";
  const usable = usableSummary(row);
  const score = scoredSummary(row);
  return [{
    key: `${POST_SESSION}:${idOf(row.rowKey) ?? JSON.stringify([row.studentEmail, row.sentAt, row.quizTitle])}`,
    source: POST_SESSION,
    kind: completed ? "completed" : "claimed",
    at: validTime(completed ? row.completedAt : row.claimedAt),
    studentUserId: idOf(row.studentUserId),
    studentName: row.studentDisplayName || row.studentEmail || "",
    title: row.quizTitle || "Untitled quiz",
    score: score ? { correct: score.correct, total: score.total } : null,
    answered: !completed && usable ? { answered: usable.answered, total: usable.total } : null,
    row,
  }];
});

export const tagEvents = (tags) => (Array.isArray(tags) ? tags : []).flatMap((tag) => {
  if (!tag || (tag.status && tag.status !== "OPEN")) return [];
  return [{
    key: `${TAG}:${idOf(tag.id) ?? JSON.stringify([tag.studentUserId, tag.quizId, tag.questionId, tag.createdAt])}`,
    source: TAG,
    kind: "flagged",
    at: validTime(tag.createdAt),
    studentUserId: idOf(tag.studentUserId),
    studentName: tag.counterpartDisplayName || "",
    title: "",
    score: null,
    answered: null,
    tag,
  }];
});

// Newest first; undated events after every dated one. Ties keep the order the
// lists were given in, so the result is stable across renders. The first
// occurrence of a key wins.
export const mergeActivity = (...lists) => {
  const seen = new Set();
  return lists.flat()
    .filter((event) => (seen.has(event.key) ? false : seen.add(event.key)))
    .map((event, index) => ({ event, index, time: event.at ? Date.parse(event.at) : null }))
    .sort((a, b) => {
      if (a.time !== null && b.time !== null) return b.time - a.time || a.index - b.index;
      if (a.time !== null || b.time !== null) return a.time !== null ? -1 : 1;
      return a.index - b.index;
    })
    .map(({ event }) => event);
};

// Quizzes the student was sent and has not started. Not learning, but useful
// context the feed can point at the tracker for.
export const waitingCount = (rows) => (Array.isArray(rows) ? rows : [])
  .filter((row) => row && row.engagementVisible === true && ["SENT", "OPENED"].includes(row.state)).length;

const plural = (n, noun) => `${n} ${noun}${n === 1 ? "" : "s"}`;

// postSession: { enabled, data: null (loading) | "dark" | response, error }
// tags: { enabled, status: "loading" | "ok" | "dark" | "error" }
export const activityCoverage = ({ postSession = {}, tags = {} }) => {
  const loaded = [];
  const failed = [];
  const partial = [];
  let loading = false;
  let anySource = false;

  if (postSession.enabled) {
    const { data, error } = postSession;
    if (error) {
      anySource = true;
      failed.push(POST_SESSION);
    } else if (data === null || data === undefined) {
      anySource = true;
      loading = true;
    } else if (data !== "dark") {
      anySource = true;
      if (data.reason) {
        partial.push("Post-session quizzes can’t be matched to your account yet.");
      } else {
        loaded.push(data.windowDays ? `post-session quizzes (last ${data.windowDays} days)` : "post-session quizzes");
        if (data.truncated) partial.push("Only the most recent post-session quizzes were returned; older ones are not included.");
        if (data.unmatchedCount > 0) partial.push(`${plural(data.unmatchedCount, "post-session email")} could not be matched to a quiz.`);
      }
    }
  }

  if (tags.enabled) {
    if (tags.status === "error") {
      anySource = true;
      failed.push(TAG);
    } else if (tags.status === "ok") {
      anySource = true;
      loaded.push("open flagged questions");
    } else if (tags.status !== "dark") {
      anySource = true;
      loading = true;
    }
  }

  return { loaded, failed, partial, loading, anySource, allLoaded: !loading && failed.length === 0 };
};
